import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle, Loader2, AlertCircle, Users, Calendar, MapPin, Mail, KeyRound } from "lucide-react";
import ChallengeJoinFlow from "@/components/challenges/ChallengeJoinFlow";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";

interface RegistrationResult {
  registrationId: string;
  attendeeType: string;
  isTeamParticipant: boolean;
  firstName: string;
  lastName: string;
  email: string;
  ticketClassName: string;
}

const RegisterSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get("oid");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RegistrationResult | null>(null);
  
  // Team flow state
  const [showTeamFlow, setShowTeamFlow] = useState(false);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [eventId, setEventId] = useState<string | null>(null);

  // Account creation state
  const [showAccountPrompt, setShowAccountPrompt] = useState(false);
  const [accountEmail, setAccountEmail] = useState("");
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [loginPassword, setLoginPassword] = useState("");

  useEffect(() => {
    if (!orderId) {
      setError("No order ID provided. Please check your confirmation email for the correct link.");
      setLoading(false);
      return;
    }

    fetchOrderAndSetup();
  }, [orderId]);

  const fetchOrderAndSetup = async () => {
    try {
      // Check if user is already logged in
      const { data: { session } } = await supabase.auth.getSession();
      
      // First, fetch the featured challenge
      const { data: challenge } = await supabase
        .from("challenges")
        .select("id, event_id")
        .eq("is_featured", true)
        .limit(1)
        .maybeSingle();

      if (challenge) {
        setChallengeId(challenge.id);
        setEventId(challenge.event_id);
      }

      // Call edge function to fetch and process Eventbrite order
      const { data, error: fetchError } = await supabase.functions.invoke(
        "fetch-eventbrite-order",
        {
          body: { order_id: orderId },
        }
      );

      if (fetchError) {
        console.error("Error fetching order:", fetchError);
        setError("Unable to verify your registration. Please try again or contact support.");
        setLoading(false);
        return;
      }

      if (!data.success) {
        setError(data.error || "Unable to process your registration.");
        setLoading(false);
        return;
      }

      setResult(data);
      setLoading(false);

      // If already logged in, finalize and open team flow immediately
      if (session?.user && data.isTeamParticipant && challenge) {
        await finalizeAndOpenTeamFlow(session.user.id, session.user.email || data.email, data.registrationId);
        return;
      }

      // If team participant and we have a challenge, show account creation prompt
      if (data.isTeamParticipant && challenge) {
        setAccountEmail(data.email);
        setShowAccountPrompt(true);
      }
    } catch (err) {
      console.error("Error in fetchOrderAndSetup:", err);
      setError("An unexpected error occurred. Please contact support.");
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    if (!accountEmail) {
      toast.error("Please enter your email");
      return;
    }

    setCreatingAccount(true);
    try {
      // Check if already logged in
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await finalizeAndOpenTeamFlow(session.user.id, accountEmail, result?.registrationId);
        return;
      }

      // Create account with auto-confirm (no email validation needed)
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: accountEmail,
        password: crypto.randomUUID(), // Random password - user can reset later
        options: {
          emailRedirectTo: window.location.origin,
        },
      });

      if (signUpError) {
        // User already exists - show inline password login
        if (signUpError.message.includes("already registered") || 
            signUpError.message.includes("already been registered")) {
          setShowPasswordLogin(true);
          toast.info("Account exists! Please enter your password to continue.");
          setCreatingAccount(false);
          return;
        }
        throw signUpError;
      }

      if (signUpData.user) {
        // Account created AND signed in (auto-confirm enabled)
        await finalizeAndOpenTeamFlow(signUpData.user.id, accountEmail, result?.registrationId);
      }
    } catch (error: any) {
      console.error("Error creating account:", error);
      toast.error(error.message || "Failed to create account");
    } finally {
      setCreatingAccount(false);
    }
  };

  const handlePasswordLogin = async () => {
    if (!accountEmail || !loginPassword) {
      toast.error("Please enter your email and password");
      return;
    }

    setCreatingAccount(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: accountEmail,
        password: loginPassword,
      });

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          toast.error("Invalid password. Try again or use the forgot password link.");
        } else {
          toast.error(error.message);
        }
        setCreatingAccount(false);
        return;
      }

      if (data.user) {
        await finalizeAndOpenTeamFlow(data.user.id, accountEmail, result?.registrationId);
      }
    } catch (error: any) {
      console.error("Error logging in:", error);
      toast.error(error.message || "Failed to sign in");
      setCreatingAccount(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setCreatingAccount(true);
    try {
      // Store return URL for after auth
      localStorage.setItem("auth_return_url", `/register-success?oid=${orderId}`);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) throw error;
    } catch (error: any) {
      toast.error(error.message || "Failed to sign in with Google");
      setCreatingAccount(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!accountEmail) {
      toast.error("Please enter your email first");
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(accountEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast.success("Password reset link sent to your email!");
    } catch (error: any) {
      toast.error(error.message || "Failed to send reset link");
    }
  };

  const finalizeAndOpenTeamFlow = async (userId: string, email: string, registrationId?: string) => {
    try {
      // 1. Call RPC to ensure profile exists and sync data from event_registrations
      console.log("[RegisterSuccess] Calling approve_eventbrite_registrant:", { userId, email });
      
      const { data: rpcResult, error: rpcError } = await supabase.rpc('approve_eventbrite_registrant', {
        p_user_id: userId,
        p_email: email,
      });

      if (rpcError) {
        console.error("[RegisterSuccess] RPC error:", rpcError);
      } else {
        console.log("[RegisterSuccess] RPC result (profile synced):", rpcResult);
      }

      // 2. Link registration to user (if not already linked)
      if (registrationId) {
        const { error: linkError } = await supabase
          .from("event_registrations")
          .update({ user_id: userId })
          .eq("id", registrationId)
          .is("user_id", null);
          
        if (linkError) {
          console.error("[RegisterSuccess] Failed to link registration:", linkError);
        } else {
          console.log("[RegisterSuccess] Registration linked successfully");
        }
      }

      // 3. Auto-follow challenge
      if (challengeId) {
        const { error: followError } = await supabase.from("user_follows").upsert({
          user_id: userId,
          entity_type: "challenge",
          entity_id: challengeId,
        }, { onConflict: "user_id,entity_type,entity_id" });
        
        if (followError) {
          console.error("[RegisterSuccess] Failed to follow challenge:", followError);
        }
      }

      toast.success("Welcome!");
      setShowAccountPrompt(false);
      setShowTeamFlow(true);
    } catch (error) {
      console.error("[RegisterSuccess] Error in finalizeAndOpenTeamFlow:", error);
      // Still open team flow even if some steps fail
      setShowAccountPrompt(false);
      setShowTeamFlow(true);
    }
  };

  const handleTeamFlowClose = (open: boolean) => {
    setShowTeamFlow(open);
    if (!open) {
      // User closed the team flow, navigate to dashboard
      navigate("/dashboard");
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Helmet>
          <title>Processing Registration | BuildWithChrist.ai</title>
        </Helmet>
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
          <h2 className="text-xl font-semibold">Processing your registration...</h2>
          <p className="text-muted-foreground">This will only take a moment.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <Helmet>
          <title>Registration Error | BuildWithChrist.ai</title>
        </Helmet>
        <div className="text-center space-y-6 max-w-md">
          <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center mx-auto">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
          <h2 className="text-2xl font-bold">Something went wrong</h2>
          <p className="text-muted-foreground">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate("/attendees")}>
              Go to Registration
            </Button>
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-12">
      <Helmet>
        <title>Registration Confirmed | BuildWithChrist.ai</title>
      </Helmet>
      
      <div className="text-center space-y-6 max-w-lg">
        <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto">
          <CheckCircle className="w-10 h-10 text-primary" />
        </div>
        
        <div className="space-y-2">
          <h1 className="text-3xl font-bold font-display">
            {result?.isTeamParticipant ? "You're in!" : "Thank You!"}
          </h1>
          <p className="text-xl text-muted-foreground">
            {result?.firstName ? `Welcome, ${result.firstName}!` : "Your registration is confirmed."}
          </p>
        </div>

        <div className="bg-card border border-border rounded-xl p-6 text-left space-y-4">
          <h3 className="font-semibold text-lg">Registration Details</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3">
              <Users className="w-4 h-4 text-primary" />
              <span className="text-muted-foreground">Ticket Type:</span>
              <span className="font-medium">{result?.ticketClassName}</span>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 text-primary" />
              <span className="text-muted-foreground">Event:</span>
              <span className="font-medium">BuildWithChrist.ai</span>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-primary" />
              <span className="text-muted-foreground">Location:</span>
              <span className="font-medium">Phase Events Commons</span>
            </div>
          </div>
        </div>

        {/* Account Creation Prompt */}
        {showAccountPrompt && (
          <div className="bg-card border border-border rounded-xl p-6 text-left space-y-4">
            <h3 className="font-semibold text-lg">Create Your Account</h3>
            <p className="text-sm text-muted-foreground">
              Set up your account to join a team and collaborate during the event.
            </p>
            
            <div className="space-y-3">
              <div className="space-y-2">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    value={accountEmail}
                    onChange={(e) => setAccountEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-10"
                    disabled={showPasswordLogin || creatingAccount}
                  />
                </div>
              </div>

              {showPasswordLogin ? (
                <>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="pl-10"
                      disabled={creatingAccount}
                      onKeyDown={(e) => e.key === "Enter" && handlePasswordLogin()}
                    />
                  </div>
                  <Button 
                    onClick={handlePasswordLogin} 
                    disabled={creatingAccount} 
                    className="w-full"
                  >
                    {creatingAccount && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Sign In & Continue
                  </Button>
                  <Button 
                    variant="link" 
                    onClick={handleForgotPassword}
                    className="w-full text-sm"
                  >
                    Forgot password?
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => setShowPasswordLogin(false)}
                    className="w-full text-sm"
                  >
                    Use a different email
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    onClick={handleCreateAccount} 
                    disabled={creatingAccount} 
                    className="w-full"
                  >
                    {creatingAccount && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Create Account & Continue
                  </Button>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">Or</span>
                    </div>
                  </div>
                  
                  <Button 
                    variant="outline" 
                    onClick={handleGoogleSignIn}
                    disabled={creatingAccount}
                    className="w-full"
                  >
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    Continue with Google
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Non-participant message */}
        {!result?.isTeamParticipant && !showAccountPrompt && (
          <div className="space-y-4">
            <p className="text-muted-foreground">
              We've sent a confirmation email with event details. See you there!
            </p>
            <Button onClick={() => navigate("/")}>
              Back to Home
            </Button>
          </div>
        )}
      </div>

      {/* Team Flow Dialog */}
      {challengeId && (
        <ChallengeJoinFlow
          open={showTeamFlow}
          onOpenChange={handleTeamFlowClose}
          challengeId={challengeId}
          eventId={eventId}
          challengeTitle="BuildWithChrist.ai Challenge"
          initialAction="join"
          startAtStep={2}
          preRegistrationId={result?.registrationId}
        />
      )}
    </div>
  );
};

export default RegisterSuccess;
